import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { patientService } from '../services/patientService';
import { reportService } from '../services/reportService';
import { intakeFormService } from '../services/intakeFormService';
import { clinicalNoteService } from '../services/clinicalNoteService';
import { appointmentService } from '../services/appointmentService';
import { organizationService } from '../services/organizationService';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const DashboardContent = ({ user }) => {
  const navigate = useNavigate();
  const [recentPatients, setRecentPatients] = useState([]);
  const [statsData, setStatsData] = useState({
    activePatients: 0,
    inactivePatients: 0,
    totalPatients: 0
  });
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  // const [tasksData, setTasksData] = useState([]); // Hidden - Tasks feature
  const [awaitingReviewForms, setAwaitingReviewForms] = useState([]); // For Documents Awaiting Review section
  const [isLoadingAwaitingReview, setIsLoadingAwaitingReview] = useState(true);
  const [patientMap, setPatientMap] = useState({}); // Store patient ID to name mapping
  const [intakeForms, setIntakeForms] = useState([]); // For Recent Intake Forms section
  const [isLoadingIntakeForms, setIsLoadingIntakeForms] = useState(true);
  const [patientPage, setPatientPage] = useState(1);
  const [totalPatientPages, setTotalPatientPages] = useState(1);
  const patientsPerPage = 6;
  const [patientReportStatus, setPatientReportStatus] = useState({}); // Store patient ID to report status mapping
  const [unsignedNotes, setUnsignedNotes] = useState([]);
  const [checklist, setChecklist] = useState({
    hasTeam: false,
    hasClient: false,
    hasAppt: false,
    hasNote: false,
    hasInvoice: false,
  });

  // Fetch patients on component mount
  useEffect(() => {
    fetchDashboardData();
    loadUnsignedAndChecklist();
  }, []);

  const loadUnsignedAndChecklist = async () => {
    try {
      const [uq, pats, appts, members] = await Promise.all([
        clinicalNoteService.listUnsigned().catch(() => ({ data: [] })),
        patientService.getPatients({ limit: 1 }).catch(() => ({ data: [] })),
        appointmentService.list().catch(() => ({ data: [] })),
        organizationService.listMembers().catch(() => ({ data: [] })),
      ]);
      const notes = uq.data || [];
      setUnsignedNotes(notes);
      const patientList = Array.isArray(pats) ? pats : pats.data || [];
      const apptList = Array.isArray(appts) ? appts : appts.data || [];
      setChecklist({
        hasTeam: (members.data || []).length > 1,
        hasClient: patientList.length > 0,
        hasAppt: apptList.length > 0,
        hasNote: false,
        hasInvoice: false,
      });
    } catch {
      /* ignore */
    }
  };

  // Set up WebSocket connection for real-time report status updates
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    console.log('🔌 Setting up WebSocket for Dashboard');

    socket.on('connect', () => {
      console.log('✅ WebSocket connected in Dashboard');
    });

    // Helper function to refresh dashboard data
    const refreshDashboard = (reason) => {
      console.log(`🔄 Refreshing Dashboard - Reason: ${reason}`);
      // Add delay to ensure backend has saved changes
      setTimeout(() => {
        console.log('📡 Fetching fresh dashboard data from server...');
        fetchDashboardData();
      }, 1000); // Increased delay to 1 second
    };

    // Listen for report status updates
    socket.on('reportStatus', (status) => {
      console.log('📋 Report Status Update received in Dashboard:', JSON.stringify(status, null, 2));

      // Refresh dashboard data for any status change
      if (status.type === 'complete' || status.type === 'generating' || status.type === 'start') {
        console.log(`✅ Report status: ${status.type} - Refreshing dashboard`);
        refreshDashboard(`Report status: ${status.type}`);
      }
    });

    // Listen for PDF generation status updates
    socket.on('pdfStatus', (status) => {
      console.log('📄 PDF Status Update received in Dashboard:', JSON.stringify(status, null, 2));

      // Refresh dashboard data for any status change
      if (status.type === 'complete' || status.type === 'start') {
        console.log(`✅ PDF status: ${status.type} - Refreshing dashboard`);
        refreshDashboard(`PDF status: ${status.type}`);
      }
    });

    // Listen for generic notifications
    socket.on('notification', (notification) => {
      console.log('🔔 Notification received in Dashboard:', JSON.stringify(notification, null, 2));

      // Refresh dashboard for any success notification
      if (notification.type === 'success' || notification.message?.toLowerCase().includes('complete')) {
        console.log('✅ Success notification - Refreshing dashboard');
        refreshDashboard('Success notification');
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Disconnecting WebSocket in Dashboard');
      socket.off('connect');
      socket.off('reportStatus');
      socket.off('pdfStatus');
      socket.off('notification');
      socket.disconnect();
    };
  }, []);

  // Add periodic polling as a backup to ensure data freshness
  useEffect(() => {
    console.log('⏰ Setting up periodic dashboard refresh (every 15 seconds)');
    const pollInterval = setInterval(() => {
      console.log('⏰ Periodic dashboard refresh triggered');
      fetchDashboardData();
    }, 15000); // Poll every 15 seconds

    return () => {
      console.log('⏰ Clearing periodic dashboard refresh');
      clearInterval(pollInterval);
    };
  }, []);

  const fetchRecentPatients = async () => {
    try {
      setIsLoadingPatients(true);

      // Fetch recent patients sorted by creation date (newest first) with pagination
      const response = await patientService.getPatients({
        page: patientPage,
        limit: patientsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      console.log('Recent patients response:', response);

      // Handle different response structures for recent patients
      let recentPatientsData = [];
      let totalPages = 1;
      let totalCount = 0;

      if (response) {
        if (Array.isArray(response)) {
          // If API returns direct array, we need to manually paginate
          console.log('API returned direct array - need manual pagination');
          totalCount = response.length;

          // Calculate pagination manually
          const startIndex = (patientPage - 1) * patientsPerPage;
          const endIndex = startIndex + patientsPerPage;
          recentPatientsData = response.slice(startIndex, endIndex);
          totalPages = Math.ceil(totalCount / patientsPerPage);
        } else if (response.data && Array.isArray(response.data)) {
          // API properly supports pagination
          console.log('API returned paginated data');
          recentPatientsData = response.data;

          // Check for pagination info
          if (response.pagination) {
            totalPages = response.pagination.totalPages || Math.ceil((response.pagination.total || 0) / patientsPerPage);
            totalCount = response.pagination.total || response.data.length;
          } else {
            // No pagination info, might be all records
            totalPages = 1;
            totalCount = response.data.length;
          }
        }
      }

      console.log('Recent patients data (sliced):', recentPatientsData);
      console.log('Total pages:', totalPages);
      console.log('Total count:', totalCount);

      setRecentPatients(recentPatientsData);
      setTotalPatientPages(totalPages);
    } catch (error) {
      console.error('Error fetching recent patients:', error);
      setRecentPatients([]);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Fetch recent patients when page changes
  useEffect(() => {
    if (patientPage > 0) {
      fetchRecentPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientPage]);

  const fetchDashboardData = async () => {
    try {
      setIsLoadingPatients(true);
      setIsLoadingAwaitingReview(true);
      setIsLoadingIntakeForms(true);

      // Fetch all patients to calculate stats
      const allPatientsResponse = await patientService.getPatients({
        page: 1,
        limit: 10000 // Get all patients for stats
      });

      console.log('All patients response:', allPatientsResponse);

      // Handle different response structures
      let allPatients = [];
      let totalFromPagination = 0;

      if (allPatientsResponse) {
        if (Array.isArray(allPatientsResponse)) {
          // Response is directly an array
          allPatients = allPatientsResponse;
        } else if (allPatientsResponse.data && Array.isArray(allPatientsResponse.data)) {
          // Response has a data property with array
          allPatients = allPatientsResponse.data;
          // Check if there's pagination info with total count
          if (allPatientsResponse.pagination && allPatientsResponse.pagination.total) {
            totalFromPagination = allPatientsResponse.pagination.total;
          }
        }
      }

      console.log('All patients array:', allPatients);
      console.log('Total from pagination:', totalFromPagination);

      // Calculate stats from the fetched patients
      const active = allPatients.filter(p => p.status === 'active').length;
      const inactive = allPatients.filter(p => p.status !== 'active').length;
      // Use pagination total if available, otherwise use array length
      const total = totalFromPagination > 0 ? totalFromPagination : allPatients.length;

      console.log('Stats calculated:', { active, inactive, total });

      setStatsData({
        activePatients: active,
        inactivePatients: inactive,
        totalPatients: total
      });

      // Create patient map for reports and intake forms
      const pMap = {};
      allPatients.forEach(patient => {
        pMap[patient._id] = patient.fullName || `${patient.firstName} ${patient.lastName}`;
      });
      console.log('Patient map created with', Object.keys(pMap).length, 'patients:', pMap);
      setPatientMap(pMap);

      // Fetch recent patients will be handled separately by fetchRecentPatients
      await fetchRecentPatients();

      // Fetch intake forms awaiting review (draft/pending status) - for Documents Awaiting Review section
      try {
        console.log('Fetching intake forms awaiting review (backend should filter by auth token)');
        const formsResponse = await intakeFormService.getIntakeForms();
        console.log('Intake forms response for awaiting review:', formsResponse);

        if (formsResponse && Array.isArray(formsResponse)) {
          // Filter for forms that need review (draft or pending status)
          let awaitingForms = formsResponse
            .filter(form => {
              const needsReview = form.status === 'draft' || form.status === 'pending' || !form.status;
              console.log('Checking form:', form._id, 'status:', form.status, 'needs review:', needsReview);
              return needsReview;
            });

          // Additional client-side filtering: Only show forms that belong to patients in patientMap
          const userPatientIds = Object.keys(patientMap);
          console.log('User patient IDs for forms:', userPatientIds);

          if (userPatientIds.length > 0) {
            awaitingForms = awaitingForms.filter(form => {
              const belongsToUser = userPatientIds.includes(form.patientId);
              console.log(`Form ${form._id} patientId: ${form.patientId}, belongs to current user: ${belongsToUser}`);
              return belongsToUser;
            });
          }

          // Sort by creation date (newest first) and get the first 5
          awaitingForms = awaitingForms
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5);

          console.log('Final awaiting review forms for dashboard:', awaitingForms);
          setAwaitingReviewForms(awaitingForms);
        } else {
          console.log('No forms response or not an array');
          setAwaitingReviewForms([]);
        }
      } catch (formsError) {
        console.error('Error fetching forms awaiting review:', formsError);
        setAwaitingReviewForms([]);
      } finally {
        setIsLoadingAwaitingReview(false);
      }

      // Fetch intake forms (recent 5) - backend should filter by auth token
      try {
        console.log('Fetching intake forms (backend should filter by auth token)');
        const intakeFormsResponse = await intakeFormService.getIntakeForms();
        console.log('Intake forms response:', intakeFormsResponse);

        if (intakeFormsResponse && Array.isArray(intakeFormsResponse)) {
          // Get the first 5 intake forms sorted by creation date
          const recentIntakeForms = intakeFormsResponse
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5);
          console.log('Recent intake forms for dashboard:', recentIntakeForms);
          setIntakeForms(recentIntakeForms);

          // Create a mapping of patient ID to their most recent report status
          const reportStatusMap = {};

          // Sort forms by creation date (newest first) and group by patient
          const sortedForms = [...intakeFormsResponse].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
          );

          // For each patient, get the status of their most recent form
          sortedForms.forEach(form => {
            if (form.patientId && !reportStatusMap[form.patientId]) {
              // Only set the status for the first (most recent) form we encounter for this patient
              reportStatusMap[form.patientId] = form.status || 'draft';
            }
          });

          console.log('Patient report status map:', reportStatusMap);
          setPatientReportStatus(reportStatusMap);
        }
      } catch (intakeFormError) {
        console.error('Error fetching intake forms:', intakeFormError);
        setIntakeForms([]);
      } finally {
        setIsLoadingIntakeForms(false);
      }

      // Tasks feature hidden
      // setTasksData([
      //   { task: 'Send reports', dueDate: getTodayDate(), priority: 'High', assignedTo: user?.name || 'Dr. Millar' },
      //   { task: 'Call PCP', dueDate: getTodayDate(), priority: 'High', assignedTo: user?.name || 'Dr. Millar' },
      //   { task: 'Review patient files', dueDate: getTodayDate(), priority: 'Normal', assignedTo: user?.name || 'Dr. Millar' },
      //   { task: 'Update records', dueDate: getTodayDate(), priority: 'Low', assignedTo: user?.name || 'Dr. Millar' }
      // ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Hidden - getTodayDate was used for tasks feature
  // const getTodayDate = () => {
  //   const today = new Date();
  //   return today.toLocaleDateString('en-GB'); // Format: DD/MM/YYYY
  // };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // Format: DD/MM/YYYY
  };

  const getPatientNameById = (patientId) => {
    if (!patientId) {
      console.log('getPatientNameById: No patientId provided');
      return 'Unknown Patient';
    }

    console.log('getPatientNameById: Looking for patient', patientId);
    console.log('getPatientNameById: PatientMap has', Object.keys(patientMap).length, 'entries');

    // First, try to find patient in patientMap (contains all patients)
    if (patientMap[patientId]) {
      console.log('getPatientNameById: Found in patientMap:', patientMap[patientId]);
      return patientMap[patientId];
    }

    // Fallback: Try to find patient in recent patients list
    const patient = recentPatients.find(p => p._id === patientId);
    if (patient) {
      console.log('getPatientNameById: Found in recentPatients:', patient.fullName || `${patient.firstName} ${patient.lastName}`);
      return patient.fullName || `${patient.firstName} ${patient.lastName}`;
    }

    console.log('getPatientNameById: Patient not found, returning Unknown Patient');
    return 'Unknown Patient';
  };

  const messagesData = [
    { name: 'Dave Grayson', initials: 'DG', message: 'Lorem ipsum dolor sit amet.', time: 'Just Now', unread: true }
  ];

  // Hidden - Tasks feature
  // const getPriorityClass = (priority) => {
  //   switch (priority) {
  //     case 'High': return 'bg-red-100 text-danger';
  //     case 'Normal': return 'bg-yellow-100 text-warning';
  //     case 'Low': return 'bg-green-100 text-low';
  //     default: return 'bg-gray-100 text-gray-300';
  //   }
  // };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:gap-4 xl:gap-5 p-3 sm:p-4 xl:p-5 bg-gray-100 min-h-[calc(100vh-70px)] overflow-y-auto max-w-[2000px] mx-auto w-full">
      {/* Account Profile Card */}
      {user && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 xl:p-6 shadow-sm">
          <div className="flex flex-col gap-4">
            {/* Top Section - User Info and Basic Details */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* User Info Section */}
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-primary/20">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl sm:text-3xl font-poppins font-bold text-primary">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>

                {/* User Details */}
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-poppins font-semibold text-gray-900 mb-1">
                    {user?.name || 'User'}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-2">{user?.email}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {user?.role || 'User'}
                    </span>
                    {user?.selectedPlan && user?.hasCompletedPayment ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-[#25b6dd] to-[#1a8ba8] text-white capitalize">
                        {user?.selectedPlan} Plan
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Free Trial
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* View Full Profile Button */}
              <div className="flex items-center justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('View Profile button clicked - navigating to settings');
                    navigate('/settings');
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-poppins font-medium hover:bg-primary/90 transition-colors w-full sm:w-auto cursor-pointer"
                >
                  View Profile
                </button>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200"></div>

            {/* Onboarding checklist for new orgs */}
            {(!checklist.hasClient || !checklist.hasAppt || unsignedNotes.length > 0) && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
                <div className="font-medium text-primary mb-2">Getting started</div>
                <ul className="space-y-1 text-gray-700">
                  <li>{checklist.hasTeam ? '✓' : '○'} Invite a team member (Settings → Team)</li>
                  <li>
                    {checklist.hasClient || statsData.totalPatients > 0 ? '✓' : '○'}{' '}
                    <button type="button" className="underline" onClick={() => navigate('/patient/new')}>
                      Add a client
                    </button>
                  </li>
                  <li>
                    {checklist.hasAppt ? '✓' : '○'}{' '}
                    <button type="button" className="underline" onClick={() => navigate('/appointments')}>
                      Schedule a visit
                    </button>
                  </li>
                  <li>
                    ○{' '}
                    <button type="button" className="underline" onClick={() => navigate('/progress-notes')}>
                      Write &amp; sign a progress note
                    </button>
                  </li>
                  <li>
                    ○{' '}
                    <button type="button" className="underline" onClick={() => navigate('/billing')}>
                      Create an invoice
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {/* Unsigned / missing notes queue */}
            {unsignedNotes.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-amber-900 text-sm">
                    Unsigned notes ({unsignedNotes.length})
                  </h3>
                  <button
                    type="button"
                    className="text-xs text-primary underline"
                    onClick={() => navigate('/progress-notes')}
                  >
                    Open Progress Notes
                  </button>
                </div>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {unsignedNotes.slice(0, 8).map((n) => (
                    <li key={n._id} className="text-sm text-amber-900 flex justify-between gap-2">
                      <span className="truncate">
                        {n.title || n.noteType || 'Note'} ·{' '}
                        {n.patientId?.fullName || 'Client'}
                      </span>
                      <button
                        type="button"
                        className="text-xs underline shrink-0"
                        onClick={() =>
                          navigate(`/patient/${n.patientId?._id || n.patientId}`)
                        }
                      >
                        Chart
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bottom Section - Account Details */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Organization */}
              {(user?.clinicName || user?.organization) && (
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">Organization</p>
                  <p className="text-sm font-poppins font-semibold text-gray-900 truncate">
                    {user?.clinicName || user?.organization}
                  </p>
                </div>
              )}

              {/* License/NPI */}
              {(user?.licenseNo || user?.npiType1) && (
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">License/NPI</p>
                  <p className="text-sm font-poppins font-semibold text-gray-900">
                    {user?.licenseNo || user?.npiType1}
                  </p>
                </div>
              )}

              {/* Phone */}
              {(user?.phone || user?.phoneNumber) && (
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p className="text-sm font-poppins font-semibold text-gray-900">
                    {user?.phone || user?.phoneNumber}
                  </p>
                </div>
              )}

              {/* Subscription Plan Price - Only show if user has completed payment */}
              {user?.selectedPlan && user?.planPrice && user?.planPrice > 0 && user?.hasCompletedPayment && (
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">Plan Price</p>
                  <p className="text-sm font-poppins font-semibold text-green-600">
                    ${user?.planPrice}/{user?.billingFrequency || 'monthly'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Stats Row - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4 items-stretch">
        {/* First Stats Card */}
        <div className="col-span-1 xl:col-span-4 bg-white border border-gray-200 rounded-lg p-4 xl:p-5 text-center h-[140px] sm:h-[150px] xl:h-[157px] flex flex-col justify-center">
          <h3 className="mb-3 xl:mb-4 font-poppins font-medium text-lg xl:text-xl text-black leading-tight">Total Active Patients</h3>
          <div className="text-3xl xl:text-4xl font-medium mb-2 text-primary">{statsData.activePatients}</div>
          <p className="font-poppins text-xs xl:text-sm text-gray-300 m-0">Inactive Patients: {statsData.inactivePatients}</p>
        </div>

        {/* Second Stats Card */}
        <div className="col-span-1 xl:col-span-4 bg-white border border-gray-200 rounded-lg p-4 xl:p-5 text-center h-[140px] sm:h-[150px] xl:h-[157px] flex flex-col justify-center">
          <h3 className="mb-3 xl:mb-4 font-poppins font-medium text-lg xl:text-xl text-black leading-tight">Total Patients</h3>
          <div className="text-3xl xl:text-4xl font-medium mb-2 text-primary">{statsData.totalPatients}</div>
          <p className="font-poppins text-xs xl:text-sm text-gray-300 m-0">All patients in the system</p>
        </div>

        {/* Quick Actions */}
        <div className="col-span-1 md:col-span-2 xl:col-span-4 h-[140px] sm:h-[150px] xl:h-[157px] bg-white border border-gray-200 rounded-lg p-4 xl:p-5 text-center flex flex-col justify-center">
          <h3 className="mb-3 xl:mb-4 font-poppins font-medium text-lg xl:text-xl text-black leading-tight">Quick Actions</h3>
          <div className="grid grid-cols-3 sm:flex sm:flex-row items-center justify-center gap-2 sm:gap-1.5 xl:gap-2">
            <button onClick={() => navigate('/patient/new')} className="flex flex-col items-center justify-center gap-1 sm:gap-1 xl:gap-2 bg-transparent border-none cursor-pointer hover:bg-primary/5 rounded-lg p-2 sm:p-1 xl:p-2 transition-colors min-w-0">
              <div className="w-8 sm:w-9 xl:w-11 h-8 sm:h-9 xl:h-11 rounded-lg flex items-center justify-center border border-gray-200 text-primary bg-white flex-shrink-0">
                <svg className="w-5 sm:w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" strokeWidth="2"/>
                  <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="text-black text-[10px] sm:text-xs xl:text-sm text-center leading-tight whitespace-nowrap">Add Patient</span>
            </button>

            <button className="flex flex-col items-center justify-center gap-1 sm:gap-1 xl:gap-2 bg-transparent border-none cursor-pointer hover:bg-primary/5 rounded-lg p-2 sm:p-1 xl:p-2 transition-colors min-w-0">
              <div className="w-8 sm:w-9 xl:w-11 h-8 sm:h-9 xl:h-11 rounded-lg flex items-center justify-center border border-gray-200 text-primary bg-white flex-shrink-0">
                <svg className="w-5 sm:w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2"/>
                  <polygon points="22,2 15,22 11,13 2,9 22,2" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="text-black text-[10px] sm:text-xs xl:text-sm text-center leading-tight whitespace-nowrap">Send Report</span>
            </button>

            <button className="flex flex-col items-center justify-center gap-1 sm:gap-1 xl:gap-2 bg-transparent border-none cursor-pointer hover:bg-primary/5 rounded-lg p-2 sm:p-1 xl:p-2 transition-colors min-w-0">
              <div className="w-8 sm:w-9 xl:w-11 h-8 sm:h-9 xl:h-11 rounded-lg flex items-center justify-center border border-gray-200 text-primary bg-white flex-shrink-0">
                <svg className="w-5 sm:w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="11" x2="12" y2="17" stroke="currentColor" strokeWidth="2"/>
                  <line x1="9" y1="14" x2="15" y2="14" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="text-black text-[10px] sm:text-xs xl:text-sm text-center leading-tight whitespace-nowrap">Create<br/>Assessment</span>
            </button>

            <button onClick={() => navigate('/form-builder')} className="flex flex-col items-center justify-center gap-1 sm:gap-1 xl:gap-2 bg-transparent border-none cursor-pointer hover:bg-primary/5 rounded-lg p-2 sm:p-1 xl:p-2 transition-colors min-w-0">
              <div className="w-8 sm:w-9 xl:w-11 h-8 sm:h-9 xl:h-11 rounded-lg flex items-center justify-center border border-gray-200 text-primary bg-white flex-shrink-0">
                <svg className="w-5 sm:w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="text-black text-[10px] sm:text-xs xl:text-sm text-center leading-tight whitespace-nowrap">Create<br/>integration</span>
            </button>

           
          </div>
        </div>
      </div>

      {/* Main Content Area - Responsive */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] 2xl:grid-cols-[1fr_450px] gap-3 sm:gap-4 xl:gap-5">
        {/* Content Grid */}
        <div className="flex flex-col gap-4 xl:gap-5">
          {/* Documents Awaiting Review - Intake Forms */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 xl:p-5 border-b border-gray-200">
              <h3 className="m-0 font-poppins font-medium text-base sm:text-lg xl:text-xl text-black">Documents Awaiting Review ({awaitingReviewForms.length})</h3>
            </div>
            <div className="overflow-x-auto">
              {isLoadingAwaitingReview ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">Loading forms...</p>
                </div>
              ) : awaitingReviewForms.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">No forms awaiting review</p>
                </div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Form ID</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Patient Name</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Submitted Date</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {awaitingReviewForms.map((form, index) => (
                      <tr
                        key={form._id || index}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/intake-form/view/${form._id}`)}
                      >
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          #{form._id?.slice(-8) || 'N/A'}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {form.fullName || patientMap[form.patientId] || 'N/A'}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {formatDate(form.createdAt)}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusBadgeClass(form.status)}`}>
                            {form.status || 'draft'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Tasks Table - Hidden */}
          {/* <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 xl:p-5 border-b border-gray-200">
              <h3 className="m-0 font-poppins font-medium text-base sm:text-lg xl:text-xl text-black">Tasks Due Today (5)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Tasks</th>
                    <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Due Date</th>
                    <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Priority</th>
                    <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Assigned to</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksData.map((task, index) => (
                    <tr key={index}>
                      <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">{task.task}</td>
                      <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">{task.dueDate}</td>
                      <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                        <span className={`px-2 py-2 rounded-lg text-sm text-center min-w-[56px] inline-block ${getPriorityClass(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">{task.assignedTo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> */}

          {/* Intake Forms */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 xl:p-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="m-0 font-poppins font-medium text-base sm:text-lg xl:text-xl text-black">Recent Intake Forms ({intakeForms.length})</h3>
              <button
                onClick={() => navigate('/intake-forms')}
                className="text-primary text-sm font-poppins hover:underline"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              {isLoadingIntakeForms ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">Loading intake forms...</p>
                </div>
              ) : intakeForms.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">No intake forms found</p>
                </div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Patient Name</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Date Created</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Status</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Chief Complaint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intakeForms.map((form, index) => (
                      <tr
                        key={form._id || index}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/intake-form/view/${form._id}`)}
                      >
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {form.fullName || patientMap[form.patientId] || 'N/A'}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {formatDate(form.createdAt)}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusBadgeClass(form.status)}`}>
                            {form.status || 'draft'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {form.chiefComplaint || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Recently Added Patients */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 xl:p-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="m-0 font-poppins font-medium text-base sm:text-lg xl:text-xl text-black">Recently Added Patients</h3>
              <span className="text-sm text-gray-500 font-poppins">
                Page {patientPage} of {totalPatientPages}
              </span>
            </div>
            <div className="overflow-x-auto">
              {isLoadingPatients ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">Loading patients...</p>
                </div>
              ) : recentPatients.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-poppins">No patients found</p>
                </div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Patient Name</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Date Added</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Contact</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Status</th>
                      <th className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 text-left font-poppins font-medium text-xs sm:text-sm xl:text-base text-black border-b border-gray-200 bg-white">Report Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPatients.map((patient, index) => (
                      <tr
                        key={patient._id || index}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {patient.fullName || `${patient.firstName} ${patient.lastName}`}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {formatDate(patient.createdAt)}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {patient.phoneNumber || patient.email || 'N/A'}
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            patient.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {patient.status || 'active'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 xl:px-5 py-2 sm:py-3 xl:py-4 font-poppins text-xs sm:text-sm text-black border-b border-gray-200">
                          {patientReportStatus[patient._id] ? (
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusBadgeClass(patientReportStatus[patient._id])}`}>
                              {patientReportStatus[patient._id]}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                              No Report
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {!isLoadingPatients && recentPatients.length > 0 && totalPatientPages > 1 && (
              <div className="p-3 sm:p-4 xl:p-5 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => setPatientPage(prev => Math.max(1, prev - 1))}
                  disabled={patientPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-poppins font-medium transition-colors ${
                    patientPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  Previous
                </button>

                <div className="flex gap-2">
                  {[...Array(totalPatientPages)].map((_, index) => {
                    const pageNum = index + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      pageNum === 1 ||
                      pageNum === totalPatientPages ||
                      (pageNum >= patientPage - 1 && pageNum <= patientPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPatientPage(pageNum)}
                          className={`px-3 py-2 rounded-lg text-sm font-poppins font-medium transition-colors ${
                            patientPage === pageNum
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === patientPage - 2 || pageNum === patientPage + 2) {
                      return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => setPatientPage(prev => Math.min(totalPatientPages, prev + 1))}
                  disabled={patientPage === totalPatientPages}
                  className={`px-3 py-2 rounded-lg text-sm font-poppins font-medium transition-colors ${
                    patientPage === totalPatientPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Messages Sidebar */}
        <div className="bg-white border border-gray-200 rounded-lg h-fit max-h-[600px] xl:max-h-[757px] overflow-hidden">
          <div className="p-3 sm:p-4 xl:p-5 border-b border-gray-200">
            <h3 className="m-0 font-poppins font-medium text-base sm:text-lg xl:text-xl text-black">Recent Messages</h3>
          </div>
          <div className="px-4 sm:px-6 xl:px-10 py-4 sm:py-6 xl:py-8 pb-3.5 flex flex-col gap-4 xl:gap-5 max-h-[550px] xl:max-h-[682px] overflow-y-auto">
            {messagesData.map((message, index) => (
              <div key={index} className="flex gap-2 pb-3 xl:pb-5 border-b border-gray-200 last:border-b-0 last:pb-0">
                <div className="flex-shrink-0">
                  <div className="w-10 xl:w-12 h-10 xl:h-12 rounded-full bg-primary flex items-center justify-center font-poppins font-semibold text-base xl:text-xl text-white border border-gray-200">
                    {message.initials}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-0.5">
                    <span className="font-poppins font-medium text-sm xl:text-base text-black">{message.name}</span>
                    <div className="flex items-center gap-1">
                      <span className="font-poppins font-medium text-[10px] xl:text-xs text-primary whitespace-nowrap">{message.time}</span>
                      {message.unread && <div className="w-1.5 xl:w-2 h-1.5 xl:h-2 rounded-full bg-primary flex-shrink-0"></div>}
                    </div>
                  </div>
                  <p className="m-0 font-poppins text-[10px] xl:text-xs text-gray-300 leading-relaxed">{message.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;